import { useMutation } from "@tanstack/react-query";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

import type { ArtifactDetail } from "@/api/artifacts";
import { apiDownload, apiFetch, ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Download an artifact's payload files (flowstack#619).
 *
 * Before this, a completed run's outputs were visible in the portal but
 * unreachable — the only ways to retrieve a result were the SDK or pulling the
 * bundle straight out of S3.
 *
 * The payload list is fetched **on click**, not on render: this button sits in
 * every row of the catalog table, and an eager detail query per row would turn
 * one page view into N requests for data most rows never need.
 *
 * Fetch-detail → download is ONE mutation rather than a chained pair, so the
 * component has a single `{isPending, isError}` surface and one place to
 * render the busy state.
 */
export interface ArtifactDownloadButtonProps {
  contentHash: string;
  /** Rendered compactly (icon-only) inside table rows. */
  compact?: boolean;
  className?: string;
}

export function ArtifactDownloadButton({
  contentHash,
  compact = false,
  className,
}: ArtifactDownloadButtonProps): JSX.Element {
  const download = useMutation({
    mutationFn: async () => {
      const detail = await apiFetch<ArtifactDetail>(
        `/api/artifacts/${encodeURIComponent(contentHash)}`
      );
      const payloads = detail.payloads ?? [];
      if (payloads.length === 0) {
        // Not an error: most typed objects are JSON-only and have no payload
        // file. Say so plainly rather than failing with a confusing 404.
        return { downloaded: 0 };
      }
      // Sequential, not Promise.all: each save triggers a browser download
      // prompt, and firing N at once is what makes browsers block them.
      for (const payload of payloads) {
        await apiDownload(payload.download_url, payload.payload_name);
      }
      return { downloaded: payloads.length };
    },
    onSuccess: ({ downloaded }) => {
      if (downloaded === 0) {
        toast.info("This artifact has no downloadable payload file.");
      } else if (downloaded > 1) {
        toast.success(`Downloading ${downloaded} files.`);
      }
    },
    onError: (error: unknown) => {
      const message =
        error instanceof ApiError && error.message
          ? error.message
          : "Download failed. Please try again.";
      toast.error(message);
    },
  });

  return (
    <Button
      type="button"
      variant="ghost"
      size={compact ? "sm" : "default"}
      className={cn(className)}
      disabled={download.isPending}
      // The row itself navigates to the artifact detail; stop the click here so
      // downloading never doubles as navigation.
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        download.mutate();
      }}
      aria-label={`Download payload for artifact ${contentHash}`}
    >
      {download.isPending ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <Download className="h-4 w-4" aria-hidden />
      )}
      {!compact && <span className="ml-2">Download</span>}
    </Button>
  );
}
